import { useContext, useState, useRef, useEffect, useCallback } from "react"
import axios from "axios"
import { useQuery } from "@apollo/client"
import ParticipantSidebar from "../../components/sidebars/ParticipantSidebar"
import { TabView, TabPanel } from "primereact/tabview"
import { Button } from "primereact/button"
import { Toast } from "primereact/toast"
import {
  showError,
  Actions,
  getActiveReferral,
  updateActiveReferral,
  GET_USER,
  inactiveParticipant,
} from "./Utils/Utils"
import EmploymentReferralIndex from "./Referral"
import EmploymentJobIndex from "./Job/index"
import EmploymentReferral from "./Referral/ReferralForm"
import FollowAlongSupportIndex from "./FollowAlongSupport"
import { UserContext } from "../../context/userContext"
import { employmentApiUrl } from "../../config/microservicesUrls"
import { LoaderMedium } from "../../components/Loaders"
import styles from "../../styles/Participant-Employment.module.css"
import CareerProfileIndex from "./Career/index"
import NoData from "./Utils/NoData"
import ParticipantProfileBoxSmall from "../../components/ParticipantProfileBoxSmall"

const api_url = employmentApiUrl()

export default function EmploymentIndex() {
  const toast = useRef(null)
  const userCtx = useContext(UserContext)
  const [referrals, setReferrals] = useState([])
  const [loading, setLoading] = useState(true)
  const [showReferralForm, setShowReferralForm] = useState(false)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [isActiveReferral, setIsActiveReferral] = useState(false)
  const [activeCareerProfile, setActiveCareerProfile] = useState(false)
  const [referralFormAction, setReferralFormAction] = useState(Actions.Add)
  const [selectedReferral, setSelectedReferral] = useState(null)
  const [showTransitionPlan, setShowTransitionPlan] = useState(false)
  const [viewMode, setViewMode] = useState(null)
  const inactive = inactiveParticipant(userCtx?.participant_membership_status)
  const [activeReferral, setActiveReferral] = useState([])
  const [transitionPlan, setTransitionPlan] = useState(null)

  const { refetch } = useQuery(GET_USER, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
    skip: true,
  })

  const getStaffName = async (staff_id) => {
    if (!staff_id || !userCtx?.session?.user?.organization_id) {
      console.warn(
        "Staff ID or Organization ID are incomplete, Page:employment.",
      )
      return null
    }

    const res = await refetch({
      staff_id: staff_id,
      organization_id: userCtx.session.user.organization_id,
    })
    const { staff_name_first, staff_name_last } = res.data.user
    return { first: staff_name_first, last: staff_name_last }
  }
  const updateSelectedReferral = useCallback(
    async (referral) => {
      if (referral) {
        if (
          typeof referral.createdBy !== "object" &&
          typeof referral.updatedBy !== "object"
        ) {
          setLoading(true)
          const createdBy = await getStaffName(referral?.createdBy)
          const updatedBy =
            referral.updatedBy != null
              ? await getStaffName(referral?.updatedBy)
              : null
          referral.createdBy = createdBy
          referral.updatedBy = updatedBy
          setLoading(false)
        }
        setSelectedReferral(referral)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedReferral],
  )
  useEffect(() => {
    updateSelectedReferral()
  }, [updateSelectedReferral])
  const updateReferrals = async (data) => {
    setLoading(true)
    setShowReferralForm(false)
    let participantReferrals = data
    let activeReferral = getActiveReferral(participantReferrals)
    userCtx.setCurrentActiveReferral(activeReferral?.id)
    if (activeReferral != null) {
      const createdBy = await getStaffName(activeReferral.createdBy)
      const updatedBy =
        activeReferral.updatedBy != null
          ? await getStaffName(activeReferral.updatedBy)
          : null
      activeReferral.createdBy = createdBy
      activeReferral.updatedBy = updatedBy
      participantReferrals = updateActiveReferral(
        participantReferrals,
        activeReferral,
      )
    }
    const active = getActiveReferral(participantReferrals)
    setActiveReferral(active)
    setReferrals(participantReferrals)
    setLoading(false)
  }
  const getParticipantReferrals = () => {
    setLoading(true)
    axios
      .get(
        `${api_url}/referral/${userCtx.session.user.organization_id}/${userCtx.current_participant_id}`,
        {
          headers: {
            Authorization: `Bearer ${userCtx.tokens.getEmploymentToken()}`,
          },
        },
      )
      .then(async (response) => {
        await updateReferrals(response.data)
      })
      .catch(function (response) {
        const errors = response["response"]["data"]["errors"]
        setReferrals([])
        setLoading(false)
        showError(toast, "Not able to get referrals", errors)
      })
  }

  useEffect(() => {
    getParticipantReferrals()
    setActiveTabIndex(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.current_participant_id])

  useEffect(() => {
    if (activeTabIndex === 0) {
      //Reload referrals
      getParticipantReferrals()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabIndex])

  const renderNoReferral = () => {
    const descText =
      "Creating an employment referral is the first step to enrolling the participant and gaining access to their career profile, job search plan, and more."
    return (
      <div className={styles.no_record_div}>
        <NoData />
        <div style={{ width: "415px" }}>
          <p style={{ fontWeight: "700" }}>
            Please create an employment services internal referral
          </p>
          <p style={{ marginTop: "15px" }}>{descText}</p>
        </div>
        <Button
          className={styles.noDataBtn}
          label="Add employment referral"
          onClick={() => setShowReferralForm(true)}
          disabled={inactive}
        />
      </div>
    )
  }
  const closeViewMode = () => {
    setShowReferralForm(false)
    setViewMode(null)
    setSelectedReferral(null)
    setActiveTabIndex(0)
  }
  const updateData = (data) => {
    if (referralFormAction === Actions.Add) {
      const updatedReferrals = [...referrals]
      updatedReferrals.unshift(data)
      updateReferrals(updatedReferrals)
    } else {
      const updatedRecords = referrals.map((r) => {
        if (r.id === data.id) {
          return data
        }
        return r
      })
      setReferrals(updatedRecords)
    }
  }
  const renderEmploymentTabView = () => {
    return (
      <>
        {!loading && !showReferralForm && activeReferral === null
          ? !viewMode && (
              <Button
                label="Create employment referral"
                className={styles.referral_btn}
                onClick={() => {
                  setReferralFormAction(Actions.Add)
                  setSelectedReferral(null)
                  setShowReferralForm(true)
                }}
              />
            )
          : !showReferralForm &&
            !showTransitionPlan &&
            activeTabIndex === 0 && (
              <Button
                className={styles.referral_btn}
                style={{ marginTop: "10px" }}
                label="Add employment transition plan"
                onClick={() => {
                  setShowTransitionPlan(true)
                  setReferralFormAction(Actions.Edit)
                }}
                disabled={inactive}
              />
            )}
        <TabView
          className="custom_tabview"
          activeIndex={activeTabIndex}
          onTabChange={(e) => setActiveTabIndex(e.index)}
        >
          <TabPanel header="Employment referral">
            <EmploymentReferralIndex
              referrals={referrals}
              loading={loading}
              setReferrals={(data) => updateReferrals(data)}
              inactive={inactive}
              updateIsActiveReferral={(value) => {
                setIsActiveReferral(value)
              }}
              updateHasCareerProfile={(value) => {
                setActiveCareerProfile(value)
              }}
              setViewMode={setViewMode}
              viewMode={viewMode}
              showReferralForm={showReferralForm}
              setShowReferralForm={setShowReferralForm}
              activeReferral={activeReferral}
              referralFormAction={referralFormAction}
              setReferralFormAction={setReferralFormAction}
              selectedReferral={selectedReferral}
              setSelectedReferral={setSelectedReferral}
              setShowTransitionPlan={setShowTransitionPlan}
              showTransitionPlan={showTransitionPlan}
              updateSelectedReferral={updateSelectedReferral}
              closeViewMode={closeViewMode}
              setTransitionPlan={setTransitionPlan}
              transitionPlan={transitionPlan}
              updateData={updateData}
            />
          </TabPanel>
          <TabPanel
            header="Career profile"
            disabled={!isActiveReferral && !viewMode}
          >
            <CareerProfileIndex
              updateActiveCareer={setActiveCareerProfile}
              inactive={inactive}
              viewMode={viewMode}
              closeViewMode={closeViewMode}
            />
          </TabPanel>
          <TabPanel
            header="Jobs"
            disabled={(!isActiveReferral || !activeCareerProfile) && !viewMode}
          >
            <EmploymentJobIndex
              inactive={inactive}
              viewMode={viewMode}
              closeViewMode={closeViewMode}
            />
          </TabPanel>
          <TabPanel
            header="Follow along supports"
            disabled={(!isActiveReferral || !activeCareerProfile) && !viewMode}
          >
            <FollowAlongSupportIndex
              inactive={inactive}
              viewMode={viewMode}
              closeViewMode={closeViewMode}
            />
          </TabPanel>
        </TabView>
      </>
    )
  }
  return (
    <div>
      <Toast ref={toast} position="top-right" />
      <div className="layout-with-sidebar">
        <div className="sidebar">
          <ParticipantSidebar />
        </div>
        <div className="main-content">
          <ParticipantProfileBoxSmall />
          {loading && (
            <div>
              <LoaderMedium />
            </div>
          )}
          {!loading &&
            !showReferralForm &&
            referrals.length === 0 &&
            renderNoReferral()}
          {!loading &&
            referrals.length > 0 &&
            (!showReferralForm || viewMode) &&
            renderEmploymentTabView()}
          {!loading && showReferralForm && !viewMode && (
            <EmploymentReferral
              action={referralFormAction}
              referral={activeReferral}
              closeForm={() => setShowReferralForm(false)}
              updateData={async (data) => {
                setViewMode(null)
                updateData(data)
              }}
              setTransitionPlan={setTransitionPlan}
              transitionPlan={transitionPlan}
            />
          )}
        </div>
      </div>
    </div>
  )
}
